/**
 * guide/index.php:38-44 `_md()` — the markdown the Informations entries are
 * written in: **bold**, *italic*, [text](url), and line breaks. Parsed into
 * tokens here and rendered as React elements (Markdown.tsx), never as HTML.
 *
 * One deliberate difference: PHP turns ANY [text](url) into a link, so an
 * entry holding [x](javascript:…) would run script on a guest page. Only
 * http(s), mailto:, tel: and site-relative targets become links here; any
 * other target renders as its plain text.
 */
export type MdNode =
  | { t: 'text'; v: string }
  | { t: 'strong'; c: MdNode[] }
  | { t: 'em'; c: MdNode[] }
  | { t: 'link'; href: string; c: MdNode[] }
  | { t: 'br' };

const BOLD = /\*\*(.+?)\*\*/;
const LINK = /\[([^\]]+)\]\(([^)]+)\)/;
const ITALIC = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/;

export function safeHref(url: string): string | null {
  const u = url.trim();
  if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
  if (u.startsWith('/') && !u.startsWith('//')) return u;
  return null;
}

function inline(s: string): MdNode[] {
  if (!s) return [];
  // The earliest match wins; PHP's replacement order (bold, italic, link)
  // breaks ties.
  const candidates = [
    { kind: 'strong' as const, m: BOLD.exec(s) },
    { kind: 'em' as const, m: ITALIC.exec(s) },
    { kind: 'link' as const, m: LINK.exec(s) },
  ].filter((c): c is { kind: 'strong' | 'em' | 'link'; m: RegExpExecArray } => c.m !== null);
  if (candidates.length === 0) return [{ t: 'text', v: s }];
  const first = candidates.reduce((a, b) => (b.m.index < a.m.index ? b : a));
  const { m, kind } = first;
  const before = s.slice(0, m.index);
  const after = s.slice(m.index + m[0].length);
  let node: MdNode;
  if (kind === 'link') {
    const href = safeHref(m[2] ?? '');
    node = href ? { t: 'link', href, c: inline(m[1] ?? '') } : { t: 'text', v: m[1] ?? '' };
  } else {
    node = { t: kind, c: inline(m[1] ?? '') };
  }
  return [...(before ? [{ t: 'text' as const, v: before }] : []), node, ...inline(after)];
}

export function parseMiniMarkdown(text: string): MdNode[] {
  const out: MdNode[] = [];
  text.split(/\r\n|\r|\n/).forEach((line, i) => {
    if (i > 0) out.push({ t: 'br' });
    out.push(...inline(line));
  });
  return out;
}
